import { registerPlugin } from "@capacitor/core"
import { type LiveStatusAdapter, type LiveStatusTokenEvent, getLiveStatusAdapter } from "./liveStatus"
import { notifyLiveStatusPolicyChanged } from "./liveStatusCoordinator"
import { applyMobileLiveStatusPolicy, getMobileLiveStatusPolicy, markMobileLiveStatusPolicyUnavailable } from "./mobilePolicy"
import type { NativeMobilePlatform, PlatformAdapter, PlatformInfo, PlatformListener, PushRegistrationEvent, PushTransport } from "./types"

const CONTRACT_VERSION = 1
const DEFAULT_GATEWAY_ORIGIN = "https://push.wildlark.ai"
const REQUEST_TIMEOUT_MS = 15_000
const POLICY_RETRY_MS = 60_000
const MAX_POLICY_REFRESH_MS = 60 * 60_000

interface PushIdentity {
    keyId: string
    format: "spki-p256" | "x963-p256"
    publicKey: string
}

interface PendingEnrollment {
    version: 1
    status: "pending"
    nodeId: string
    deviceRef: string
    gatewayOrigin: string
    claim: string
    updatedAt: string
}

interface ActiveEnrollment {
    version: 1
    status: "enrolled"
    nodeId: string
    deviceRef: string
    gatewayOrigin: string
    channelId: string
    installationId: string
    providerTokenUpdatedAt?: string
    activityTokenClaim: string
    activityTokenExpiresAt: string
    updatedAt: string
}

export type PushEnrollment = PendingEnrollment | ActiveEnrollment

export type PushEnrollmentDiagnosticState = "not-started" | "not-enrolled" | "pending" | "enrolled" | "failed"

export interface PushEnrollmentDiagnostics {
    state: PushEnrollmentDiagnosticState
    pendingCount: number
    enrolledCount: number
    installationIdSuffix: string | null
    providerTokenUpdatedAt: string | null
}

interface SecurePushPlugin {
    getOrCreatePushIdentity(): Promise<unknown>
    signPushPayload(options: { payload: string }): Promise<unknown>
    savePushClaim(options: { nodeId: string; deviceRef: string; gatewayOrigin: string; claim: string }): Promise<unknown>
    completePushEnrollment(options: {
        nodeId: string
        channelId: string
        installationId: string
        activityTokenClaim: string
        activityTokenExpiresAt: string
    }): Promise<unknown>
    recordPushTokenUpdate(options: { nodeId: string }): Promise<unknown>
    listPushEnrollments(): Promise<unknown>
    removePushEnrollment(options: { nodeId: string }): Promise<unknown>
}

interface ClaimResponse {
    channelId: string
    installationId: string
    activityTokenClaim: string
    activityTokenExpiresAt: string
}

const securePushPlugin = registerPlugin<SecurePushPlugin>("WildLarkSecureRegistry")

export class PushEnrollmentCoordinator {
    private readonly gatewayOrigin: string
    private readonly liveStatus: LiveStatusAdapter
    private readonly fetcher: typeof fetch
    private readonly securePush: SecurePushPlugin
    private registrationListener: PlatformListener | null = null
    private liveTokenListener: PlatformListener | null = null
    private providerToken: { transport: PushTransport; token: string } | null = null
    private queue: Promise<void> = Promise.resolve()
    private disposed = false
    private releaseInfo: PlatformInfo | null = null
    private policyTimer: ReturnType<typeof globalThis.setTimeout> | null = null

    constructor(
        private readonly platform: PlatformAdapter,
        private readonly kind: NativeMobilePlatform,
        options?: { gatewayOrigin?: string; liveStatus?: LiveStatusAdapter; fetcher?: typeof fetch; securePlugin?: SecurePushPlugin }
    ) {
        this.gatewayOrigin = normalizeGatewayOrigin(options?.gatewayOrigin ?? import.meta.env.VITE_WILDLARK_PUSH_GATEWAY_URL ?? DEFAULT_GATEWAY_ORIGIN)
        this.liveStatus = options?.liveStatus ?? getLiveStatusAdapter(kind)
        this.fetcher = options?.fetcher ?? fetch
        this.securePush = options?.securePlugin ?? securePushPlugin
    }

    async start(): Promise<void> {
        const [registrationListener, liveTokenListener] = await Promise.all([
            this.platform.addPushRegistrationListener((event) => this.handleRegistration(event)),
            this.liveStatus.addTokenListener((event) => this.enqueue(() => this.submitActivityToken(event))),
        ])
        if (this.disposed) {
            await Promise.all([registrationListener.remove(), liveTokenListener.remove()])
            return
        }
        this.registrationListener = registrationListener
        this.liveTokenListener = liveTokenListener
        const enrollments = await this.list()
        if (enrollments.length > 0) await this.platform.registerForPushNotifications()
        await this.refreshMobilePolicy()
        await this.configureActivityEndpoints(enrollments)
    }

    async prepareClaim(nodeId: string, deviceRef: string, claim: string): Promise<void> {
        await this.securePush.savePushClaim({
            nodeId: requiredString(nodeId, "nodeId"),
            deviceRef: requiredString(deviceRef, "deviceRef"),
            gatewayOrigin: this.gatewayOrigin,
            claim: requiredString(claim, "claim"),
        })
        if (this.providerToken) await this.enrollPending(this.providerToken)
        else await this.platform.registerForPushNotifications()
    }

    async reconcileOnResume(): Promise<void> {
        const enrollments = await this.list()
        if (enrollments.length > 0) await this.platform.registerForPushNotifications()
        await this.refreshMobilePolicy()
        await this.configureActivityEndpoints(enrollments)
        if (this.providerToken) await this.reconcileToken(this.providerToken)
    }

    async syncPreferences(): Promise<void> {
        const preferences = await this.preferences()
        for (const enrollment of await this.list()) {
            if (enrollment.status !== "enrolled") continue
            const response = record(
                await this.signedJSON(enrollment, `/v1/installations/${encodeURIComponent(enrollment.installationId)}/preferences`, "PUT", preferences),
                "preference response"
            )
            if (response.status !== "updated") throw new Error("Push gateway returned an invalid preference result")
            if (response.liveStatusEnd !== undefined) {
                const batch = record(response.liveStatusEnd, "live-status end result")
                if (batch.status !== "done" || batch.failed !== 0) throw new Error("Push gateway could not end every disabled live-status surface")
            }
        }
    }

    async revokeCurrentDevice(nodeId: string, deviceRef: string): Promise<{ status: "revoked" | "already-revoked" | "not-enrolled" | "not-current-device" }> {
        const enrollment = (await this.list()).find((candidate) => candidate.nodeId === nodeId && candidate.deviceRef === deviceRef)
        if (!enrollment) return { status: "not-current-device" }
        if (enrollment.status !== "enrolled") {
            await this.securePush.removePushEnrollment({ nodeId })
            return { status: "not-enrolled" }
        }
        const result = await this.signedJSON(
            enrollment,
            `/v1/installations/${encodeURIComponent(enrollment.installationId)}/memberships/${encodeURIComponent(enrollment.channelId)}`,
            "DELETE",
            {}
        )
        const source = record(result, "revocation response")
        if (source.status !== "revoked" && source.status !== "already-revoked") throw new Error("Push gateway returned an invalid revocation status")
        await this.securePush.removePushEnrollment({ nodeId })
        return { status: source.status }
    }

    async list(): Promise<PushEnrollment[]> {
        return parsePushEnrollmentList(await this.securePush.listPushEnrollments())
    }

    async getDiagnostics(nodeId: string | null): Promise<PushEnrollmentDiagnostics> {
        return summarizePushEnrollments(await this.list(), nodeId)
    }

    dispose(): void {
        this.disposed = true
        if (this.registrationListener) void this.registrationListener.remove()
        if (this.liveTokenListener) void this.liveTokenListener.remove()
        this.registrationListener = null
        this.liveTokenListener = null
        if (this.policyTimer) globalThis.clearTimeout(this.policyTimer)
        this.policyTimer = null
    }

    private handleRegistration(event: PushRegistrationEvent): void {
        if (event.type === "failed") {
            console.warn("[mobile-push] Native provider registration failed")
            return
        }
        const providerToken = { transport: event.transport, token: event.token }
        this.providerToken = providerToken
        this.enqueue(() => this.reconcileToken(providerToken))
    }

    private async reconcileToken(providerToken: { transport: PushTransport; token: string }): Promise<void> {
        await this.enrollPending(providerToken)
        const release = await this.currentReleaseInfo()
        for (const enrollment of await this.list()) {
            if (enrollment.status !== "enrolled") continue
            await this.signedJSON(enrollment, `/v1/installations/${encodeURIComponent(enrollment.installationId)}/notification-token`, "PUT", {
                ...providerToken,
                ...(release.appVersion ? { appVersion: release.appVersion } : {}),
                ...(release.appBuild ? { appBuild: release.appBuild } : {}),
            })
            await this.securePush.recordPushTokenUpdate({ nodeId: enrollment.nodeId })
        }
    }

    private async enrollPending(providerToken: { transport: PushTransport; token: string }): Promise<void> {
        const identity = await this.identity()
        const preferences = await this.preferences()
        const release = await this.currentReleaseInfo()
        for (const enrollment of await this.list()) {
            if (enrollment.status !== "pending") continue
            const response = await this.request(enrollment.gatewayOrigin, "/v1/enrollments/claim", {
                method: "POST",
                body: JSON.stringify({
                    version: CONTRACT_VERSION,
                    claim: enrollment.claim,
                    deviceRef: enrollment.deviceRef,
                    installation: {
                        ...identity,
                        platform: this.kind,
                        appVersion: release.appVersion ?? "",
                        appBuild: release.appBuild ?? "",
                    },
                    notification: providerToken,
                    preferences,
                }),
            })
            const claimed = parseClaimResponse(await response.json())
            const completed = parseEnrollment(
                await this.securePush.completePushEnrollment({
                    nodeId: enrollment.nodeId,
                    channelId: claimed.channelId,
                    installationId: claimed.installationId,
                    activityTokenClaim: claimed.activityTokenClaim,
                    activityTokenExpiresAt: claimed.activityTokenExpiresAt,
                })
            )
            if (completed.status === "enrolled") await this.configureActivityEndpoint(completed)
        }
    }

    private async submitActivityToken(event: LiveStatusTokenEvent): Promise<void> {
        if (!getMobileLiveStatusPolicy().liveStatusEnabled) return
        for (const enrollment of await this.list()) {
            if (enrollment.status !== "enrolled" || this.kind !== "ios") continue
            const route = `/v1/activity-tokens/${encodeURIComponent(enrollment.activityTokenClaim)}`
            await this.request(enrollment.gatewayOrigin, route, {
                method: "PUT",
                body: JSON.stringify({
                    version: CONTRACT_VERSION,
                    id: "wildlark-running-work",
                    kind: event.type,
                    ...(event.type === "activity-update" ? { activityId: event.activityId } : {}),
                    token: event.token,
                }),
            })
        }
    }

    private async configureActivityEndpoints(enrollments: PushEnrollment[]): Promise<void> {
        if (this.kind !== "ios" || !getMobileLiveStatusPolicy().liveStatusEnabled) return
        for (const enrollment of enrollments) if (enrollment.status === "enrolled") await this.configureActivityEndpoint(enrollment)
    }

    private async configureActivityEndpoint(enrollment: ActiveEnrollment): Promise<void> {
        const route = `/v1/activity-tokens/${encodeURIComponent(enrollment.activityTokenClaim)}`
        const result = await this.liveStatus.configureRemoteTokenEndpoint(`${enrollment.gatewayOrigin}${route}`)
        if (result.status === "failed") throw new Error(result.message)
    }

    private async identity(): Promise<PushIdentity> {
        const source = record(await this.securePush.getOrCreatePushIdentity(), "push identity response")
        const identity = record(source.identity, "push identity")
        const format = identity.format
        if (format !== "spki-p256" && format !== "x963-p256") throw new Error("Push installation key format is invalid")
        return { keyId: requiredString(identity.keyId, "keyId"), format, publicKey: requiredString(identity.publicKey, "publicKey") }
    }

    private async refreshMobilePolicy(): Promise<void> {
        try {
            const release = await this.currentReleaseInfo()
            const query = new URLSearchParams({ platform: this.kind })
            if (release.appVersion) query.set("appVersion", release.appVersion)
            if (release.appBuild) query.set("appBuild", release.appBuild)
            const controller = new AbortController()
            const timeout = globalThis.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
            try {
                const response = await this.fetcher(`${this.gatewayOrigin}/v1/mobile-policy?${query.toString()}`, {
                    method: "GET",
                    headers: { Accept: "application/json" },
                    signal: controller.signal,
                })
                if (!response.ok) throw new Error("Mobile policy request failed")
                applyMobileLiveStatusPolicy(await response.json(), release)
            } finally {
                globalThis.clearTimeout(timeout)
            }
        } catch {
            markMobileLiveStatusPolicyUnavailable()
        }
        notifyLiveStatusPolicyChanged()
        this.scheduleMobilePolicyRefresh()
    }

    private scheduleMobilePolicyRefresh(): void {
        if (this.policyTimer) globalThis.clearTimeout(this.policyTimer)
        this.policyTimer = null
        if (this.disposed) return
        const policy = getMobileLiveStatusPolicy()
        const expiresAt = policy.expiresAt ? Date.parse(policy.expiresAt) : Number.NaN
        const delay = Number.isFinite(expiresAt) ? Math.min(MAX_POLICY_REFRESH_MS, Math.max(1_000, expiresAt - Date.now())) : POLICY_RETRY_MS
        this.policyTimer = globalThis.setTimeout(() => {
            this.policyTimer = null
            this.enqueue(() => this.refreshMobilePolicy())
        }, delay)
    }

    private async currentReleaseInfo(): Promise<PlatformInfo> {
        if (this.releaseInfo) return this.releaseInfo
        try {
            const info = await this.platform.getInfo()
            if (info.kind !== this.kind || !info.native) throw new Error("Native release metadata does not match the mobile platform")
            this.releaseInfo = info
        } catch {
            this.releaseInfo = { kind: this.kind, native: true }
        }
        return this.releaseInfo
    }

    private async preferences(): Promise<{ monitorRunningWork: boolean; showTaskTitles: boolean }> {
        const [monitoring, titles] = await Promise.all([this.liveStatus.getMonitoringEnabled(), this.liveStatus.getTaskTitlesEnabled()])
        return {
            monitorRunningWork: monitoring.status === "ok" && monitoring.value,
            showTaskTitles: titles.status !== "ok" || titles.value,
        }
    }

    private async signedJSON(enrollment: ActiveEnrollment, route: string, method: "PUT" | "DELETE", value: unknown): Promise<unknown> {
        const body = JSON.stringify(value)
        const identity = await this.identity()
        const timestamp = String(Math.floor(Date.now() / 1_000))
        const nonce = randomNonce()
        const canonical = await canonicalRequest(method, route, timestamp, nonce, body)
        const signatureSource = record(await this.securePush.signPushPayload({ payload: canonical }), "push signature response")
        const signature = requiredString(signatureSource.signature, "signature")
        const response = await this.request(enrollment.gatewayOrigin, route, {
            method,
            body,
            headers: {
                "WildLark-Key-Id": identity.keyId,
                "WildLark-Timestamp": timestamp,
                "WildLark-Nonce": nonce,
                "WildLark-Signature": signature,
            },
        })
        return response.json()
    }

    private async request(
        gatewayOrigin: string,
        route: string,
        options: { method: "POST" | "PUT" | "DELETE"; body: string; headers?: Record<string, string> }
    ): Promise<Response> {
        const controller = new AbortController()
        const timeout = globalThis.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
        try {
            const response = await this.fetcher(`${normalizeGatewayOrigin(gatewayOrigin)}${route}`, {
                method: options.method,
                body: options.body,
                headers: { "Content-Type": "application/json", ...options.headers },
                signal: controller.signal,
            })
            if (!response.ok) throw new Error(`Push gateway request failed (${await responseErrorCode(response)})`)
            return response
        } finally {
            globalThis.clearTimeout(timeout)
        }
    }

    private enqueue(operation: () => Promise<void>): void {
        const run = async () => {
            if (this.disposed) return
            try {
                await operation()
            } catch {
                console.warn("[mobile-push] Push enrollment reconciliation failed")
            }
        }
        this.queue = this.queue.then(run, run)
    }
}

let activeCoordinator: PushEnrollmentCoordinator | null = null

export function setActivePushEnrollmentCoordinator(coordinator: PushEnrollmentCoordinator | null): void {
    activeCoordinator = coordinator
}

export function getActivePushEnrollmentCoordinator(): PushEnrollmentCoordinator | null {
    return activeCoordinator
}

export async function getActivePushEnrollmentDiagnostics(nodeId: string | null): Promise<PushEnrollmentDiagnostics> {
    if (!activeCoordinator) {
        return {
            state: "not-started",
            pendingCount: 0,
            enrolledCount: 0,
            installationIdSuffix: null,
            providerTokenUpdatedAt: null,
        }
    }
    try {
        return await activeCoordinator.getDiagnostics(nodeId)
    } catch {
        return {
            state: "failed",
            pendingCount: 0,
            enrolledCount: 0,
            installationIdSuffix: null,
            providerTokenUpdatedAt: null,
        }
    }
}

export async function revokeActivePushEnrollment(
    nodeId: string,
    deviceRef: string
): Promise<{ status: "revoked" | "already-revoked" | "not-enrolled" | "not-current-device" | "not-mobile" }> {
    if (!activeCoordinator) return { status: "not-mobile" }
    return activeCoordinator.revokeCurrentDevice(nodeId, deviceRef)
}

export async function syncActivePushPreferences(): Promise<void> {
    await activeCoordinator?.syncPreferences()
}

export async function canonicalRequest(method: string, route: string, timestamp: string, nonce: string, body: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(body))
    const bodyHash = [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("")
    return ["WILDLARK-PUSH-V1", method.toUpperCase(), route, timestamp, nonce, bodyHash].join("\n")
}

export function parsePushEnrollmentList(value: unknown): PushEnrollment[] {
    const source = record(value, "push enrollment list")
    if (source.version !== CONTRACT_VERSION || !Array.isArray(source.enrollments)) throw new Error("Push enrollment list is invalid")
    return source.enrollments.map(parseEnrollmentRecord)
}

export function summarizePushEnrollments(enrollments: PushEnrollment[], nodeId: string | null): PushEnrollmentDiagnostics {
    const relevant = enrollments.filter((enrollment) => !nodeId || enrollment.nodeId === nodeId)
    const pendingCount = relevant.filter((enrollment) => enrollment.status === "pending").length
    const enrolled = relevant.filter((enrollment): enrollment is ActiveEnrollment => enrollment.status === "enrolled")
    let newest: ActiveEnrollment | null = null
    for (const candidate of enrolled) {
        if (!newest || Date.parse(candidate.providerTokenUpdatedAt ?? candidate.updatedAt) > Date.parse(newest.providerTokenUpdatedAt ?? newest.updatedAt)) {
            newest = candidate
        }
    }
    return {
        state: enrolled.length > 0 ? "enrolled" : pendingCount > 0 ? "pending" : "not-enrolled",
        pendingCount,
        enrolledCount: enrolled.length,
        installationIdSuffix: newest ? newest.installationId.slice(-8) : null,
        providerTokenUpdatedAt: newest?.providerTokenUpdatedAt ?? null,
    }
}

function parseEnrollment(value: unknown): PushEnrollment {
    const source = record(value, "push enrollment response")
    return parseEnrollmentRecord(source.enrollment)
}

function parseEnrollmentRecord(value: unknown): PushEnrollment {
    const source = record(value, "push enrollment")
    if (source.version !== CONTRACT_VERSION) throw new Error("Push enrollment version is invalid")
    const common = {
        version: 1 as const,
        nodeId: requiredString(source.nodeId, "nodeId"),
        deviceRef: requiredString(source.deviceRef, "deviceRef"),
        gatewayOrigin: normalizeGatewayOrigin(requiredString(source.gatewayOrigin, "gatewayOrigin")),
        updatedAt: isoTimestamp(source.updatedAt, "updatedAt"),
    }
    if (source.status === "pending") return { ...common, status: "pending", claim: requiredString(source.claim, "claim") }
    if (source.status === "enrolled") {
        return {
            ...common,
            status: "enrolled",
            channelId: requiredString(source.channelId, "channelId"),
            installationId: requiredString(source.installationId, "installationId"),
            ...(source.providerTokenUpdatedAt === undefined
                ? {}
                : { providerTokenUpdatedAt: isoTimestamp(source.providerTokenUpdatedAt, "providerTokenUpdatedAt") }),
            activityTokenClaim: requiredString(source.activityTokenClaim, "activityTokenClaim"),
            activityTokenExpiresAt: isoTimestamp(source.activityTokenExpiresAt, "activityTokenExpiresAt"),
        }
    }
    throw new Error("Push enrollment status is invalid")
}

function parseClaimResponse(value: unknown): ClaimResponse {
    const source = record(value, "push claim response")
    if (source.version !== CONTRACT_VERSION) throw new Error("Push claim response version is invalid")
    return {
        channelId: requiredString(source.channelId, "channelId"),
        installationId: requiredString(source.installationId, "installationId"),
        activityTokenClaim: requiredString(source.activityTokenClaim, "activityTokenClaim"),
        activityTokenExpiresAt: isoTimestamp(source.activityTokenExpiresAt, "activityTokenExpiresAt"),
    }
}

function record(value: unknown, label: string): Record<string, unknown> {
    if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${label} is invalid`)
    return value as Record<string, unknown>
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Push enrollment is missing ${field}`)
    return value
}

function isoTimestamp(value: unknown, field: string): string {
    const result = requiredString(value, field)
    if (!Number.isFinite(Date.parse(result))) throw new Error(`Push enrollment has invalid ${field}`)
    return result
}

function normalizeGatewayOrigin(value: string): string {
    const parsed = new URL(value)
    if (parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.pathname !== "/" || parsed.search || parsed.hash) {
        throw new Error("Push gateway must be an HTTPS origin")
    }
    return parsed.origin
}

function randomNonce(): string {
    const value = new Uint8Array(18)
    crypto.getRandomValues(value)
    let binary = ""
    for (const byte of value) binary += String.fromCharCode(byte)
    return `nonce_${btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "")}`
}

async function responseErrorCode(response: Response): Promise<string> {
    try {
        const source = record(await response.json(), "gateway error")
        const error = record(source.error, "gateway error")
        return typeof error.code === "string" && error.code ? error.code : `http_${response.status}`
    } catch {
        return `http_${response.status}`
    }
}
